/**
 * Alexia Shop - Vue Router Configuration
 * ========================================
 * Route definitions for the SPA.
 */

import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// Lazy-loaded views
const HomeView = () => import('@/views/HomeView.vue')
const ProductsView = () => import('@/views/products/ProductsView.vue')
const ProductDetailView = () => import('@/views/products/ProductDetailView.vue')
const CategoryView = () => import('@/views/products/CategoryView.vue')
const CollectionsView = () => import('@/views/products/CollectionsView.vue')
const CollectionView = () => import('@/views/products/CollectionView.vue')
const SearchView = () => import('@/views/products/SearchView.vue')

const CartView = () => import('@/views/cart/CartView.vue')
const CheckoutView = () => import('@/views/checkout/CheckoutView.vue')
const OrderConfirmationView = () => import('@/views/checkout/OrderConfirmationView.vue')

const LoginView = () => import('@/views/auth/LoginView.vue')
const RegisterView = () => import('@/views/auth/RegisterView.vue')
const ForgotPasswordView = () => import('@/views/auth/ForgotPasswordView.vue')
const ResetPasswordView = () => import('@/views/auth/ResetPasswordView.vue')
const VerifyEmailView = () => import('@/views/auth/VerifyEmailView.vue')
const GoogleCallbackView = () => import('@/views/auth/GoogleCallbackView.vue')
const InstagramCallbackView = () => import('@/views/auth/InstagramCallbackView.vue')

const AccountView = () => import('@/views/account/AccountView.vue')
const OrdersView = () => import('@/views/account/OrdersView.vue')
const OrderDetailView = () => import('@/views/account/OrderDetailView.vue')
const WishlistView = () => import('@/views/account/WishlistView.vue')
const AddressesView = () => import('@/views/account/AddressesView.vue')
const SettingsView = () => import('@/views/account/SettingsView.vue')

const AboutView = () => import('@/views/pages/AboutView.vue')
const ContactView = () => import('@/views/pages/ContactView.vue')
const FAQView = () => import('@/views/pages/FAQView.vue')

const BlogView = () => import('@/views/blog/BlogView.vue')
const BlogPostView = () => import('@/views/blog/BlogPostView.vue')

const PrivacyView = () => import('@/views/legal/PrivacyView.vue')
const TermsView = () => import('@/views/legal/TermsView.vue')
const CookiesView = () => import('@/views/legal/CookiesView.vue')
const ShippingView = () => import('@/views/legal/ShippingView.vue')
const ReturnsView = () => import('@/views/legal/ReturnsView.vue')

const NotFoundView = () => import('@/views/NotFoundView.vue')

// Admin views (lazy-loaded)
const AdminLayout = () => import('@/views/admin/AdminLayout.vue')
const AdminDashboard = () => import('@/views/admin/AdminDashboard.vue')
const AdminProductsView = () => import('@/views/admin/ProductsView.vue')
const AdminProductFormView = () => import('@/views/admin/ProductFormView.vue')
const AdminCategoriesView = () => import('@/views/admin/CategoriesView.vue')
const AdminCollectionsView = () => import('@/views/admin/CollectionsView.vue')
const AdminOrdersView = () => import('@/views/admin/OrdersView.vue')
const AdminOrderDetailView = () => import('@/views/admin/OrderDetailView.vue')
const AdminCustomersView = () => import('@/views/admin/CustomersView.vue')
const AdminReviewsView = () => import('@/views/admin/ReviewsView.vue')
const AdminBlogPostsView = () => import('@/views/admin/BlogPostsView.vue')
const AdminBlogFormView = () => import('@/views/admin/BlogFormView.vue')
const AdminPromoCodesView = () => import('@/views/admin/PromoCodesView.vue')
const AdminMediaView = () => import('@/views/admin/MediaView.vue')
const AdminContentView = () => import('@/views/admin/ContentView.vue')
const AdminTranslationsView = () => import('@/views/admin/TranslationsView.vue')

// Route definitions
const routes: RouteRecordRaw[] = [
  // ============================================
  // PUBLIC ROUTES
  // ============================================
  {
    path: '/',
    name: 'home',
    component: HomeView,
    meta: { title: 'Aura by Alexia | Joyeria Artesanal' }
  },

  // Products
  {
    path: '/products',
    name: 'products',
    component: ProductsView,
    meta: { title: 'Productos' }
  },
  {
    path: '/products/:id',
    name: 'product-detail',
    component: ProductDetailView,
    props: true,
    meta: { title: 'Producto' }
  },
  {
    path: '/category/:slug',
    name: 'category',
    component: CategoryView,
    props: true,
    meta: { title: 'Categoria' }
  },
  {
    path: '/collections',
    name: 'collections',
    component: CollectionsView,
    meta: { title: 'Colecciones' }
  },
  {
    path: '/collection/:slug',
    name: 'collection',
    component: CollectionView,
    props: true,
    meta: { title: 'Coleccion' }
  },
  {
    path: '/search',
    name: 'search',
    component: SearchView,
    meta: { title: 'Buscar' }
  },

  // Cart & Checkout
  {
    path: '/cart',
    name: 'cart',
    component: CartView,
    meta: { title: 'Carrito' }
  },
  {
    path: '/checkout',
    name: 'checkout',
    component: CheckoutView,
    meta: { title: 'Checkout', requiresAuth: false } // Guest checkout allowed
  },
  {
    path: '/order-confirmation/:orderNumber',
    name: 'order-confirmation',
    component: OrderConfirmationView,
    props: true,
    meta: { title: 'Pedido Confirmado' }
  },

  // Auth
  {
    path: '/auth/login',
    name: 'login',
    component: LoginView,
    meta: { title: 'Iniciar Sesion', guestOnly: true }
  },
  {
    path: '/auth/register',
    name: 'register',
    component: RegisterView,
    meta: { title: 'Crear Cuenta', guestOnly: true }
  },
  {
    path: '/auth/forgot-password',
    name: 'forgot-password',
    component: ForgotPasswordView,
    meta: { title: 'Recuperar Contrasena', guestOnly: true }
  },
  {
    path: '/auth/reset-password',
    name: 'reset-password',
    component: ResetPasswordView,
    meta: { title: 'Nueva Contrasena', guestOnly: true }
  },
  {
    path: '/auth/verify-email',
    name: 'verify-email',
    component: VerifyEmailView,
    meta: { title: 'Verificar Email' }
  },
  {
    path: '/auth/google/callback',
    name: 'google-callback',
    component: GoogleCallbackView,
    meta: { title: 'Google Login' }
  },
  {
    path: '/auth/instagram/callback',
    name: 'instagram-callback',
    component: InstagramCallbackView,
    meta: { title: 'Instagram Login' }
  },

  // Account (requires auth)
  {
    path: '/account',
    name: 'account',
    component: AccountView,
    meta: { title: 'Mi Cuenta', requiresAuth: true }
  },
  {
    path: '/account/orders',
    name: 'orders',
    component: OrdersView,
    meta: { title: 'Mis Pedidos', requiresAuth: true }
  },
  {
    path: '/account/orders/:orderNumber',
    name: 'order-detail',
    component: OrderDetailView,
    props: true,
    meta: { title: 'Detalle del Pedido', requiresAuth: true }
  },
  {
    path: '/account/wishlist',
    name: 'wishlist',
    component: WishlistView,
    meta: { title: 'Lista de Deseos', requiresAuth: true }
  },
  {
    path: '/account/addresses',
    name: 'addresses',
    component: AddressesView,
    meta: { title: 'Direcciones', requiresAuth: true }
  },
  {
    path: '/account/settings',
    name: 'settings',
    component: SettingsView,
    meta: { title: 'Configuracion', requiresAuth: true }
  },

  // Static Pages
  {
    path: '/about',
    name: 'about',
    component: AboutView,
    meta: { title: 'Sobre Alexia' }
  },
  {
    path: '/contact',
    name: 'contact',
    component: ContactView,
    meta: { title: 'Contacto' }
  },
  {
    path: '/faq',
    name: 'faq',
    component: FAQView,
    meta: { title: 'Preguntas Frecuentes' }
  },

  // Blog
  {
    path: '/blog',
    name: 'blog',
    component: BlogView,
    meta: { title: 'Blog' }
  },
  {
    path: '/blog/:slug',
    name: 'blog-post',
    component: BlogPostView,
    props: true,
    meta: { title: 'Blog' }
  },

  // Legal
  {
    path: '/privacy',
    name: 'privacy',
    component: PrivacyView,
    meta: { title: 'Politica de Privacidad' }
  },
  {
    path: '/terms',
    name: 'terms',
    component: TermsView,
    meta: { title: 'Terminos y Condiciones' }
  },
  {
    path: '/cookies',
    name: 'cookies',
    component: CookiesView,
    meta: { title: 'Politica de Cookies' }
  },
  {
    path: '/shipping',
    name: 'shipping',
    component: ShippingView,
    meta: { title: 'Envios' }
  },
  {
    path: '/returns',
    name: 'returns',
    component: ReturnsView,
    meta: { title: 'Devoluciones' }
  },

  // ============================================
  // ADMIN ROUTES (requires admin)
  // ============================================
  {
    path: '/admin',
    component: AdminLayout,
    meta: { requiresAdmin: true },
    children: [
      {
        path: '',
        name: 'admin-dashboard',
        component: AdminDashboard,
        meta: { title: 'Admin - Dashboard', requiresAdmin: true }
      },
      // Products
      {
        path: 'products',
        name: 'admin-products',
        component: AdminProductsView,
        meta: { title: 'Admin - Productos', requiresAdmin: true }
      },
      {
        path: 'products/new',
        name: 'admin-product-new',
        component: AdminProductFormView,
        meta: { title: 'Admin - Nuevo Producto', requiresAdmin: true }
      },
      {
        path: 'products/:id/edit',
        name: 'admin-product-edit',
        component: AdminProductFormView,
        props: true,
        meta: { title: 'Admin - Editar Producto', requiresAdmin: true }
      },
      // Categories
      {
        path: 'categories',
        name: 'admin-categories',
        component: AdminCategoriesView,
        meta: { title: 'Admin - Categorias', requiresAdmin: true }
      },
      // Collections
      {
        path: 'collections',
        name: 'admin-collections',
        component: AdminCollectionsView,
        meta: { title: 'Admin - Colecciones', requiresAdmin: true }
      },
      // Orders
      {
        path: 'orders',
        name: 'admin-orders',
        component: AdminOrdersView,
        meta: { title: 'Admin - Pedidos', requiresAdmin: true }
      },
      {
        path: 'orders/:id',
        name: 'admin-order-detail',
        component: AdminOrderDetailView,
        props: true,
        meta: { title: 'Admin - Detalle Pedido', requiresAdmin: true }
      },
      // Promo Codes
      {
        path: 'promo-codes',
        name: 'admin-promo-codes',
        component: AdminPromoCodesView,
        meta: { title: 'Admin - Codigos Promo', requiresAdmin: true }
      },
      // Customers
      {
        path: 'customers',
        name: 'admin-customers',
        component: AdminCustomersView,
        meta: { title: 'Admin - Clientes', requiresAdmin: true }
      },
      // Reviews
      {
        path: 'reviews',
        name: 'admin-reviews',
        component: AdminReviewsView,
        meta: { title: 'Admin - Resenas', requiresAdmin: true }
      },
      // Blog
      {
        path: 'blog',
        name: 'admin-blog',
        component: AdminBlogPostsView,
        meta: { title: 'Admin - Blog', requiresAdmin: true }
      },
      {
        path: 'blog/new',
        name: 'admin-blog-new',
        component: AdminBlogFormView,
        meta: { title: 'Admin - Nueva Entrada', requiresAdmin: true }
      },
      {
        path: 'blog/:id/edit',
        name: 'admin-blog-edit',
        component: AdminBlogFormView,
        props: true,
        meta: { title: 'Admin - Editar Entrada', requiresAdmin: true }
      },
      // Content
      {
        path: 'content',
        name: 'admin-content',
        component: AdminContentView,
        meta: { title: 'Admin - Contenido', requiresAdmin: true }
      },
      // Media
      {
        path: 'media',
        name: 'admin-media',
        component: AdminMediaView,
        meta: { title: 'Admin - Multimedia', requiresAdmin: true }
      },
      // Translations
      {
        path: 'translations',
        name: 'admin-translations',
        component: AdminTranslationsView,
        meta: { title: 'Admin - Traducciones', requiresAdmin: true }
      }
    ]
  },

  // 404
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: NotFoundView,
    meta: { title: 'Pagina no encontrada' }
  }
]

// Create router
const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    }
    return { top: 0, behavior: 'smooth' }
  }
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Update document title
  document.title = (to.meta.title as string) || 'Aura by Alexia'

  // Check if route requires authentication or admin
  const requiresAuth = to.meta.requiresAuth || to.meta.requiresAdmin
  const requiresAdmin = to.meta.requiresAdmin
  const guestOnly = to.meta.guestOnly

  // Wait for auth check on protected routes
  if (requiresAuth || guestOnly) {
    // For admin routes, always verify session is still valid
    // For regular routes, only check if not already checked
    if (requiresAdmin || !authStore.isChecked) {
      try {
        await authStore.fetchUser()
      } catch {
        // Session expired or invalid
      }
    }

    // Admin routes - must be authenticated AND admin
    if (requiresAdmin) {
      if (!authStore.isAuthenticated) {
        next({ name: 'login', query: { redirect: to.fullPath } })
        return
      }
      if (!authStore.isAdmin) {
        // Redirect non-admins to home
        next({ name: 'home' })
        return
      }
    }
    // Regular protected routes
    else if (to.meta.requiresAuth && !authStore.isAuthenticated) {
      next({ name: 'login', query: { redirect: to.fullPath } })
      return
    }

    // Guest-only routes (login, register)
    if (guestOnly && authStore.isAuthenticated) {
      next({ name: 'account' })
      return
    }
  }

  next()
})

export default router
